import { useCallback, useEffect, useState } from 'react';
import { endOfMonth, format, startOfMonth, subMonths } from 'date-fns';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import {
  countNewRepeatBookings,
  type BookingCustomerRow,
} from '../lib/customerNewRepeatBookings';
import {
  fetchStandaloneInvoiceSales,
  sumStandaloneInvoiceSales,
} from '../lib/invoices/standaloneInvoiceSales';

type BookingRow = {
  id: string;
  subtotal: number | null;
  discount_amount: number | null;
};

function aggregate(rows: BookingRow[] | null) {
  const list = rows ?? [];
  const sales = list.reduce((acc, booking) => {
    const subtotal = booking.subtotal ?? 0;
    const discount = booking.discount_amount ?? 0;
    return acc + (subtotal - discount);
  }, 0);
  return { sales, count: list.length };
}

/** Current month sales and booking counts, aligned with CalendarV2. */
export function useCalendarSalesMetrics() {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;

  const [monthSales, setMonthSales] = useState(0);
  const [monthBookings, setMonthBookings] = useState(0);
  const [monthOrders, setMonthOrders] = useState(0);
  const [monthBookingsNew, setMonthBookingsNew] = useState(0);
  const [monthBookingsRepeat, setMonthBookingsRepeat] = useState(0);
  const [monthSalesLastMonth, setMonthSalesLastMonth] = useState(0);
  const [monthBookingsLastMonth, setMonthBookingsLastMonth] = useState(0);
  const [monthBookingsNewLastMonth, setMonthBookingsNewLastMonth] = useState(0);
  const [monthBookingsRepeatLastMonth, setMonthBookingsRepeatLastMonth] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setMonthSales(0);
      setMonthBookings(0);
      setMonthOrders(0);
      setMonthBookingsNew(0);
      setMonthBookingsRepeat(0);
      setMonthSalesLastMonth(0);
      setMonthBookingsLastMonth(0);
      setMonthBookingsNewLastMonth(0);
      setMonthBookingsRepeatLastMonth(0);
      setLoading(false);
      return;
    }

    const bookingFilters = (query: ReturnType<typeof supabase.from>) =>
      query
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .neq('status', 'cancelled')
        .or('in_que.is.null,in_que.eq.false');

    try {
      const now = new Date();
      const monthStart = format(startOfMonth(now), 'yyyy-MM-dd');
      const monthEnd = format(endOfMonth(now), 'yyyy-MM-dd');
      const lastMonth = subMonths(now, 1);
      const lastMonthStart = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
      const lastMonthEnd = format(endOfMonth(lastMonth), 'yyyy-MM-dd');

      const monthBookingSelect =
        'id, subtotal, discount_amount, customer_id, customer_repeat_status, end_date, created_at';

      const [monthResult, lastMonthResult, monthStandalone, lastMonthStandalone] = await Promise.all([
        bookingFilters(supabase.from('bookings').select(monthBookingSelect))
          .gte('end_date', monthStart)
          .lte('end_date', monthEnd),
        bookingFilters(supabase.from('bookings').select(monthBookingSelect))
          .gte('end_date', lastMonthStart)
          .lte('end_date', lastMonthEnd),
        // Standalone (booking-less) invoices issued in each month count as sales.
        fetchStandaloneInvoiceSales(businessId, { start: startOfMonth(now), end: endOfMonth(now) }),
        fetchStandaloneInvoiceSales(businessId, { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) }),
      ]);

      const [monthNewRepeat, lastMonthNewRepeat] = await Promise.all([
        monthResult.error
          ? Promise.resolve({ newCount: 0, repeatCount: 0 })
          : countNewRepeatBookings(
              monthResult.data as BookingCustomerRow[] | null,
              supabase,
              bookingFilters
            ),
        lastMonthResult.error
          ? Promise.resolve({ newCount: 0, repeatCount: 0 })
          : countNewRepeatBookings(
              lastMonthResult.data as BookingCustomerRow[] | null,
              supabase,
              bookingFilters
            ),
      ]);

      if (monthResult.error) {
        console.error('Error fetching month metrics:', monthResult.error);
        setMonthSales(0);
        setMonthBookings(0);
        setMonthOrders(0);
        setMonthBookingsNew(0);
        setMonthBookingsRepeat(0);
      } else {
        const monthAgg = aggregate(monthResult.data);
        setMonthSales(monthAgg.sales + sumStandaloneInvoiceSales(monthStandalone));
        setMonthBookings(monthAgg.count);
        setMonthOrders(monthStandalone.length);
        setMonthBookingsNew(monthNewRepeat.newCount);
        setMonthBookingsRepeat(monthNewRepeat.repeatCount);
      }

      if (lastMonthResult.error) {
        console.error('Error fetching last month metrics:', lastMonthResult.error);
        setMonthSalesLastMonth(0);
        setMonthBookingsLastMonth(0);
        setMonthBookingsNewLastMonth(0);
        setMonthBookingsRepeatLastMonth(0);
      } else {
        const lastMonthAgg = aggregate(lastMonthResult.data);
        setMonthSalesLastMonth(lastMonthAgg.sales + sumStandaloneInvoiceSales(lastMonthStandalone));
        setMonthBookingsLastMonth(lastMonthAgg.count);
        setMonthBookingsNewLastMonth(lastMonthNewRepeat.newCount);
        setMonthBookingsRepeatLastMonth(lastMonthNewRepeat.repeatCount);
      }
    } catch (err) {
      console.error('Error loading calendar header metrics:', err);
      setMonthSales(0);
      setMonthBookings(0);
      setMonthOrders(0);
      setMonthBookingsNew(0);
      setMonthBookingsRepeat(0);
      setMonthSalesLastMonth(0);
      setMonthBookingsLastMonth(0);
      setMonthBookingsNewLastMonth(0);
      setMonthBookingsRepeatLastMonth(0);
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId]);

  useEffect(() => {
    setLoading(true);
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  return {
    monthSales,
    monthBookings,
    /** Standalone counter-sale/order invoices counted as sales this month. */
    monthOrders,
    monthBookingsNew,
    monthBookingsRepeat,
    monthSalesLastMonth,
    monthBookingsLastMonth,
    monthBookingsNewLastMonth,
    monthBookingsRepeatLastMonth,
    loading,
    refresh: load,
  };
}
